import React from 'react';
import { Tooltip } from '@mui/material';
import HBTTableCell from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementTable/components/HBTTableCell';
import { MemoizedHbtDataTableCell } from 'Feature/V2/Shared/MemoizedHbtDataTableCell';
import { UnderwritingApprovedLenderName, TranslationType } from '../types';
import LoanStatusPill from '../../../shared/LoanStatusPill';
import styles from '../styles.module.scss';
import { loanStatusMapping } from '../../HbtMasterPoolTable/utils/LoanStatusMapping';
import { RemarkDetail } from '../../HbtMasterPool/pages/HbtMasterPoolSearchResultsPage/types';
import { isRemarkOther } from 'Feature/V2/Shared_Portfolio/utils/remarkOther';
import i18n from 'i18next';

const getUnderwritingApprovedlenderName = (
  data: UnderwritingApprovedLenderName,
  language: string
) => {
  return language === 'en' ? data.englishName : data.frenchName;
};

const getLoanStatusLabel = (status: keyof typeof loanStatusMapping, t: TranslationType) => {
  return loanStatusMapping(t)[status];
};

export const renderDefaultCell = ({ cell }: { cell: any }) => {
  const value = cell.getValue() || '';
  return (
    <MemoizedHbtDataTableCell
      dataValue={String(value)}
      cellContent={<HBTTableCell title={String(value)}>{String(value)}</HBTTableCell>}
    />
  );
};

export const renderCmhcAccountNumberCell =
  (t: TranslationType) =>
  ({ cell, row }: { cell: any; row: any }) => {
    const value = cell.getValue() || '';
    const isReducedDataSet = row.original?.reducedDatasetIndicator;
    const showReducedaDataSet = Boolean(value) && isReducedDataSet;
    return (
      <MemoizedHbtDataTableCell
        dataValue={String(value)}
        cellContent={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              whiteSpace: 'noWrap',
              overflow: 'hidden',
              textOverflow: 'ellipses'
            }}
          >
            <HBTTableCell title={String(value)}>{String(value)}</HBTTableCell>
            {showReducedaDataSet && (
              <Tooltip
                title={t('Reduced-Dataset-Tool-Tip-Text')}
                placement="right"
                arrow
                slotProps={{
                  tooltip: {
                    className: styles.loansInventoryToolTip
                  },
                  arrow: {
                    className: styles.tooltipArrow
                  }
                }}
              >
                <div
                  style={{
                    marginLeft: '8px'
                  }}
                >
                  (R)
                </div>
              </Tooltip>
            )}
          </div>
        }
      />
    );
  };

export const renderLoanStatusCell =
  (t: TranslationType) =>
  ({ cell, row }: { cell: any; row: any }) => {
    const data = cell.getValue() || '';
    const modified = row.original.riskResultOverrideIndicator === '1' ? true : false;

    return (
      <MemoizedHbtDataTableCell
        dataValue={data as string}
        cellContent={
          <HBTTableCell title={getLoanStatusLabel(data as keyof typeof loanStatusMapping, t)}>
            <LoanStatusPill
              status={data}
              label={getLoanStatusLabel(data as keyof typeof loanStatusMapping, t)}
              filled={!modified}
              modified={modified}
            />
          </HBTTableCell>
        }
      />
    );
  };

export const createUnderwritingApprovedlenderNameRenderer =
  (language: string) =>
  ({ cell }: { cell: any }) => {
    const data = cell.getValue() || '';
    const displayName = getUnderwritingApprovedlenderName(data, language);
    return (
      <MemoizedHbtDataTableCell
        dataValue={data?.englishName || ''}
        cellContent={<HBTTableCell title={displayName}>{displayName}</HBTTableCell>}
      />
    );
  };

const getRemarkValue = (remark: RemarkDetail) => {
  return isRemarkOther({ remarkID: remark.remarkId, remarkMessage: '' })
    ? remark.remarkText
    : i18n.language === 'en'
      ? remark.rationaleEnglish
      : remark.rationaleFrench;
};

export const renderRemark = ({ cell, row }: { cell: any; row: any }) => {
  const value = cell.getValue() ? getRemarkValue(cell.getValue()) : '';
  return (
    <MemoizedHbtDataTableCell
      dataValue={String(value)}
      cellContent={<HBTTableCell title={String(value)}>{String(value)}</HBTTableCell>}
    />
  );
};
